import axios from 'axios';

// Use relative URL when in development to work with proxy
const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? (process.env.REACT_APP_API_URL || 'https://api.prepstop.com')
  : ''; // Empty string means use proxy in development

// Type definitions matching backend
export interface DashboardKPIs {
  totalRegistered: number;
  activeOnPrep: number;
  pending: number;
  readyForTelehealth: number;
  pendingBreakdown: {
    hivTest: number;
    insurance: number;
    telehealth: number;
  };
  byMarket?: {
    ORLANDO: number;
    MIAMI: number;
    TAMPA: number;
    ATLANTA: number;
    OUT_OF_MARKET: number;
  };
  medicationType: {
    oral: number;
    injectable: number;
  };
  refills: number;
  renewalsDue: number;
  conversionRate: string;
}

export interface PatientListItem {
  id: number;
  firstName: string;
  lastName: string;
  email?: string;
  market: string;
  display_status: string;
  prep_type: string;
  medication_name?: string;
  assigned_pharmacy_partner_id?: number;
  assigned_community_partner_id?: number;
  assigned_campus_ambassador_id?: number;
  referral_source?: string;
  backendStatus?: string;
  statusSummary?: AdminStatusSummary;
  hiv_test_completed: boolean;
  insurance_status?: string | null;
  insurance_verified: boolean;
  telehealth_completed: boolean;
  prescription_issued: boolean;
  medication_shipped: boolean;
  injection_scheduled: boolean;
  last_dose_date?: string;
  next_refill_date?: string;
  createdAt: string;
  updatedAt: string;
}

export interface AdminStatusSummary {
  backendStatus: string;
  displayStatus: string;
  patientMessage: string;
  adminMessage: string;
  blockingIssues: string[];
  nextActions: string[];
}

export interface PatientDetail extends PatientListItem {
  state?: string;
  zip?: string;
  referral_notes?: string;
  calendlyEventUri?: string;
  calendlyInviteeUri?: string;
  prepCases?: any[];
  insurance_card_front_url?: string;
  insurance_card_back_url?: string;
  insurance_documents_uploaded_by?: number;
  insurance_documents_uploaded_at?: string;
  address_line_1?: string;
  address_line_2?: string;
  city?: string;
  phoneNumber?: string;
  insurance_provider?: string;
  insurance_policy_number?: string;
  insurance_group_id?: string;
  insurance_status?: string;
  currentHIVTest?: any;
}

export interface PharmacyQueues {
  pendingInsurance: number;
  hivResultPending: number;
  telehealthCompleted: number;
  readyForFulfillment: number;
  renewalsDue: number;
}

export interface PatientFilters {
  market?: string;
  status?: string;
  pharmacy_partner_id?: number;
  community_partner_id?: number;
  campus_ambassador_id?: number;
}

class DashboardService {
  private getAuthHeaders() {
    const token = localStorage.getItem('authToken');
    return {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      withCredentials: true,
    };
  }

  /**
   * Get dashboard KPIs (role-scoped)
   */
  async getKPIs(): Promise<DashboardKPIs> {
    const response = await axios.get(
      `${API_BASE_URL}/api/dashboard/kpis`,
      this.getAuthHeaders()
    );
    return response.data;
  }

  /**
   * Get patients list with optional filters (role-scoped)
   */
  async getPatients(filters?: PatientFilters): Promise<PatientListItem[]> {
    const params = new URLSearchParams();
    if (filters?.market) params.append('market', filters.market);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.pharmacy_partner_id) params.append('pharmacy_partner_id', filters.pharmacy_partner_id.toString());
    if (filters?.community_partner_id) params.append('community_partner_id', filters.community_partner_id.toString());
    if (filters?.campus_ambassador_id) params.append('campus_ambassador_id', filters.campus_ambassador_id.toString());

    const url = `${API_BASE_URL}/api/dashboard/patients${params.toString() ? '?' + params.toString() : ''}`;
    const response = await axios.get(url, this.getAuthHeaders());
    return response.data;
  }

  /**
   * Get detailed patient information (role-scoped)
   */
  async getPatientDetail(patientId: number): Promise<PatientDetail> {
    const url = `${API_BASE_URL}/api/dashboard/patients/${patientId}`;
    console.log('[dashboardService] getPatientDetail called with patientId:', patientId);
    console.log('[dashboardService] API_BASE_URL:', API_BASE_URL);
    console.log('[dashboardService] Full URL:', url);
    console.log('[dashboardService] NODE_ENV:', process.env.NODE_ENV);
    const response = await axios.get(url, this.getAuthHeaders());
    console.log('[dashboardService] Response status:', response.status);
    return response.data;
  }

  /**
   * Get pharmacy work queues (pharmacy partner only)
   */
  async getPharmacyQueues(): Promise<PharmacyQueues> {
    const response = await axios.get(
      `${API_BASE_URL}/api/dashboard/pharmacy/queues`,
      this.getAuthHeaders()
    );
    return response.data;
  }

  /**
   * Update patient information (admin only)
   */
  async updatePatient(patientId: number, updates: Partial<PatientDetail>): Promise<PatientDetail> {
    const response = await axios.patch(
      `${API_BASE_URL}/api/dashboard/patients/${patientId}`,
      updates,
      this.getAuthHeaders()
    );
    return response.data;
  }

  /**
   * Get patient lifecycle events
   */
  async getPatientLifecycleEvents(patientId: number): Promise<any[]> {
    const response = await axios.get(
      `${API_BASE_URL}/api/patient/${patientId}/lifecycle-events`,
      this.getAuthHeaders()
    );
    return response.data;
  }

  /**
   * Get patient medication status
   */
  async getPatientMedicationStatus(patientId: number): Promise<any> {
    const response = await axios.get(
      `${API_BASE_URL}/api/patient/${patientId}/medication-status`,
      this.getAuthHeaders()
    );
    return response.data;
  }

  /**
   * Upload patient documents
   */
  async uploadDocument(data: {
    patientId: number | string;
    documents: File[];
    documentTypes: string[];
    category: string;
    notes?: string;
  }): Promise<any> {
    const formData = new FormData();
    formData.append('patientId', String(data.patientId));
    data.documents.forEach((doc) => formData.append('documents', doc));
    data.documentTypes.forEach((type) => formData.append('documentTypes', type));
    formData.append('category', data.category);
    if (data.notes) formData.append('notes', data.notes);

    const response = await axios.post(
      `${API_BASE_URL}/api/patient-documents/upload`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          ...(localStorage.getItem('authToken') && { Authorization: `Bearer ${localStorage.getItem('authToken')}` }),
        },
        withCredentials: true,
      }
    );
    return response.data;
  }

  /**
   * Get patient documents
   */
  async getPatientDocuments(patientId: number, includeInactive: boolean = false): Promise<any> {
    const url = `${API_BASE_URL}/api/patient-documents/patient/${patientId}${includeInactive ? '?includeInactive=true' : ''}`;
    const response = await axios.get(url, this.getAuthHeaders());
    return response.data;
  }

  /**
   * Get insurance documents only
   */
  async getInsuranceDocuments(patientId: number, includeInactive: boolean = false): Promise<any> {
    const url = `${API_BASE_URL}/api/patient-documents/patient/${patientId}/insurance${includeInactive ? '?includeInactive=true' : ''}`;
    const response = await axios.get(url, this.getAuthHeaders());
    return response.data;
  }

  /**
   * Get HIV test documents only
   */
  async getHIVDocuments(patientId: number, includeInactive: boolean = false): Promise<any> {
    const url = `${API_BASE_URL}/api/patient-documents/patient/${patientId}/hiv${includeInactive ? '?includeInactive=true' : ''}`;
    const response = await axios.get(url, this.getAuthHeaders());
    return response.data;
  }

  /**
   * Verify patient insurance (pharmacy partner only)
   */
  async verifyInsurance(patientId: number): Promise<{ success: boolean; message: string }> {
    const response = await axios.post(
      `${API_BASE_URL}/api/dashboard/pharmacy/verify-insurance/${patientId}`,
      {},
      this.getAuthHeaders()
    );
    return response.data;
  }

  /**
   * Update patient insurance information
   */
  async updatePatientInsurance(patientId: number, data: {
    insurance_provider?: string;
    insurance_policy_number?: string;
    insurance_group_id?: string;
  }): Promise<{ success: boolean; message: string }> {
    const response = await axios.patch(
      `${API_BASE_URL}/api/dashboard/patients/${patientId}`,
      data,
      this.getAuthHeaders()
    );
    return response.data;
  }

  /**
   * Upload HIV test result (all roles)
   */
  async uploadHivResult(patientId: number, data: FormData): Promise<any> {
    const token = localStorage.getItem('authToken');
    const response = await axios.post(
      `${API_BASE_URL}/api/dashboard/community/upload-hiv-result/${patientId}`,
      data,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        withCredentials: true,
      }
    );
    return response.data;
  }
}

export const dashboardService = new DashboardService();
export default dashboardService;
